import React, { useRef, useEffect, useState } from 'react';
import styles from './ArrowIcon.module.scss';

export type ArrowDirection = 'up' | 'down' | 'left' | 'right';

export interface ArrowIconProps {
  /**
   * Initial arrow direction (simple mode)
   * Use this for basic usage with hover animation
   * @default 'down'
   */
  direction?: ArrowDirection;
  /**
   * Source direction for explicit transitions
   * Use with `to` prop for controlled morphing (e.g., from="left" to="right")
   * Overrides `direction` and `hoverTo` when used
   */
  from?: ArrowDirection;
  /**
   * Target direction for explicit transitions
   * Use with `from` prop for controlled morphing
   */
  to?: ArrowDirection;
  /**
   * Direction to morph to on hover (simple mode)
   * @default 'right'
   */
  hoverTo?: ArrowDirection;
  /**
   * Stroke color (CSS custom property or color value)
   * @default 'currentColor'
   */
  strokeColor?: string;
  /**
   * Icon size in pixels
   * @default 24
   */
  size?: number;
  /**
   * Stroke width
   * @default 2
   */
  strokeWidth?: number;
  /**
   * Animation duration in seconds
   * @default 0.3
   */
  animationDuration?: number;
  /**
   * Animation easing
   * @default 'power2.out'
   */
  animationEasing?: string;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Animate on hover (only applies to simple mode with direction/hoverTo)
   * @default true
   */
  animateOnHover?: boolean;
}

// Path definitions for all arrow directions (constants)
const ARROW_PATHS: Record<ArrowDirection, string[]> = {
  down: [
    'M12 4L12 12',           // top vertical line
    'M12 12L12 20',          // bottom vertical line
    'M12 20L17.6569 14.3431', // bottom-right diagonal
    'M12 20L6.34315 14.3431', // bottom-left diagonal
  ],
  right: [
    'M14 6L19.6569 11.6569',  // top-right diagonal
    'M20 12L14.3431 17.6569', // bottom-right diagonal
    'M4 12L12 12',            // left horizontal line
    'M12 12H20',              // right horizontal line
  ],
  up: [
    'M12 20L12 12',          // bottom vertical line
    'M12 12L12 4',           // top vertical line
    'M12 4L17.6569 9.6569',  // top-right diagonal
    'M12 4L6.34315 9.6569',  // top-left diagonal
  ],
  left: [
    'M10 6L4.34315 11.6569',  // top-left diagonal
    'M4 12L9.6569 17.6569',   // bottom-left diagonal
    'M20 12L12 12',           // right horizontal line
    'M12 12H4',               // left horizontal line
  ],
};

/**
 * Animated arrow icon that morphs between all four directions
 * Uses GSAP for smooth path animations
 * 
 * Usage modes:
 * 1. Simple mode: direction + hoverTo (default: down → right on hover)
 * 2. Controlled mode: from + to (explicit directional transitions)
 */
export const ArrowIcon: React.FC<ArrowIconProps> = ({
  direction = 'down',
  from,
  to,
  hoverTo = 'right',
  strokeColor = 'currentColor',
  size = 24,
  strokeWidth = 2,
  animationDuration = 0.3,
  animationEasing = 'power2.out',
  className,
  animateOnHover = true,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const pathsRef = useRef<(SVGPathElement | null)[]>([]);
  const isAnimatingRef = useRef(false);
  const currentDirectionRef = useRef<ArrowDirection>(from || direction);
  const [gsapInstance, setGsapInstance] = useState<any>(null);

  // Lazy load GSAP
  useEffect(() => {
    let isMounted = true;
    import('gsap').then(({ gsap }) => {
      if (isMounted) setGsapInstance(gsap);
    }).catch(e => console.error('Failed to load GSAP for ArrowIcon', e));
    return () => { isMounted = false; };
  }, []);

  // Determine if we're in controlled mode (from/to) or simple mode (direction/hoverTo)
  const isControlledMode = from !== undefined && to !== undefined;

  // Generic animation function for any direction
  const animateToDirection = (targetDirection: ArrowDirection) => {
    if (isAnimatingRef.current || !gsapInstance) return;
    isAnimatingRef.current = true;

    const targetPaths = ARROW_PATHS[targetDirection];

    pathsRef.current.forEach((path, index) => {
      if (path && targetPaths[index]) {
        gsapInstance.to(path, {
          attr: { d: targetPaths[index] },
          duration: animationDuration,
          ease: animationEasing,
          onComplete: () => {
            isAnimatingRef.current = false;
            currentDirectionRef.current = targetDirection;
          },
        });
      }
    });
  };

  // Set initial state (controlled mode: from/to takes precedence)
  useEffect(() => {
    const initialDirection = isControlledMode ? from! : direction;
    const initialPaths = ARROW_PATHS[initialDirection];
    
    pathsRef.current.forEach((path, index) => {
      if (path) {
        path.setAttribute('d', initialPaths[index] || '');
      }
    });
    
    currentDirectionRef.current = initialDirection;
  }, [direction, from, isControlledMode]);

  // Handle controlled mode transitions (from → to)
  useEffect(() => {
    if (isControlledMode && to && currentDirectionRef.current !== to) {
      animateToDirection(to);
    }
  }, [to, isControlledMode, animationDuration, animationEasing]);

  // Hover handlers (only for simple mode)
  const handleMouseEnter = () => {
    if (!isControlledMode && animateOnHover && hoverTo) {
      animateToDirection(hoverTo);
    }
  };

  const handleMouseLeave = () => {
    if (!isControlledMode && animateOnHover) {
      animateToDirection(direction);
    }
  };

  const containerClassName = [
    styles['arrow-icon'],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={containerClassName}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        ['--arrow-stroke-color' as string]: strokeColor,
        ['--arrow-size' as string]: `${size}px`,
      }}
    >
      <svg
        ref={svgRef}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={styles['arrow-icon__svg']}
      >
        {ARROW_PATHS.down.map((_, index) => (
          <path
            key={index}
            ref={(el) => (pathsRef.current[index] = el)}
            d=""
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            className={styles['arrow-icon__path']}
          />
        ))}
      </svg>
    </div>
  );
};

